import {Alert, Button, Spin, Table, Tag} from "antd";
import type {ColumnsType} from "antd/es/table";
import {useNavigate} from "react-router-dom";
import {AnalyticsEvent} from "../../../../utils/analytics";
import {StyledDescriptionText, StyledPageHeaderWrapper} from "../upcoming-calls.style";
import {StyledPageHeader} from "../../../../styles/dashboard/calls/calls";
import {useState} from "react";
import {useSessions, V2PaginatedResult, Session} from "../../../../features/sessions";
import {CustomPagination} from "../../../pagination";
import {StyledCreateButtonWrapper} from "../../../../layouts/dashboard-wrapper/dashboard-wrapper.style";
import {PlusOutlined} from "@ant-design/icons";
import {LoadingSpinner} from "../../../icons/icons";
import {SessionListMenuOption} from "./menu-option";
import {PaginationMeta} from "../../../../redux/service/i-service";

/**
 * Converts the v2 session pagination response (`total`/`hasNextPage`) to the
 * legacy PaginationMeta format the pagination component expects.
 */
function toPaginationMeta(
  response: V2PaginatedResult<Session>,
  page: number,
  perPage: number
): PaginationMeta {
  const totalPages = Math.ceil(response.total / perPage);
  return {
    current_page: page,
    per_page: perPage,
    total_pages: totalPages,
    next_page: response.hasNextPage ? page + 1 : 0,
    previous_page: page > 1 ? page - 1 : 0,
  };
}

interface DataType {
  key: string;
  name: string;
  price: number | string;
  currency: string;
  duration: string;
  sessions: string | null;
  status: string;
  description: string;
  tinyUrl: string;
  id: number;
  slug: string;
  isBundle: boolean;
}

const columns: ColumnsType<DataType> = [
  {
    title: "Name",
    dataIndex: "name",
    fixed: "left",
    width: "17.425rem",
    key: "name",
    render: (text, record, index) => (
      <>
        <StyledDescriptionText>{text}</StyledDescriptionText>
        {record.status === "Active" ? (
          <Tag style={{marginTop: "1rem", fontWeight: "600"}} color="green">
            Active
          </Tag>
        ) : (
          <Tag style={{marginTop: "1rem", fontWeight: "600"}}>Paused</Tag>
        )}
      </>
    ),
  },
  {
    title: "Price",
    dataIndex: "price",
    width: "17.425rem",
    key: "price",
    render: (text, record) => (
      <>
        <StyledDescriptionText>
          {record.price !== "0" && record.currency}{" "}
          {record.price === "0" ? "Free" : record.price}
        </StyledDescriptionText>
      </>
    ),
  },
  {
    title: "Duration",
    dataIndex: "duration",
    width: "17.425rem",
    key: "duration",
    render: (text, record, index) => (
      <>
        <StyledDescriptionText>{text}</StyledDescriptionText>
        {record.isBundle ? (
          <Tag style={{marginTop: "1rem", fontWeight: "600"}} color="purple">
            Bundle {record.sessions} sessions
          </Tag>
        ) : (
          <Tag style={{marginTop: "1rem", fontWeight: "600"}}>One off</Tag>
        )}
      </>
    ),
  },
  {
    title: "Description",
    key: "description",
    dataIndex: "description",
    width: "17.425rem",
    render: (text) => {
      const stripedHtml = text.replace(/<[^>]+>/g, "");
      return <StyledDescriptionText>{stripedHtml}</StyledDescriptionText>;
    },
  },
  {
    title: "",
    key: "action",
    width: "90px",
    render: (_, record) => (
      <div onClick={(e) => e.stopPropagation()}>
        <SessionListMenuOption
          serviceId={record.id}
          slug={record.slug}
          tinyUrl={record.tinyUrl}
          isBundle={record.isBundle}
        />
      </div>
    ),
  },
];

const ServicesSection = ({
  showCallCategoryModal,
}: {
  showCallCategoryModal: () => void;
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const perPage = 5;
  const {
    data: sessionsData,
    isLoading: isLoadingServicesQuery,
    isFetching: isFetchingServicesQuery,
    error: servicesQueryErrorMessage,
    isPreviousData,
  } = useSessions({page, perPage});
  if (isLoadingServicesQuery) {
    return <Spin spinning indicator={LoadingSpinner}></Spin>;
  }

  if (servicesQueryErrorMessage) {
    return (
      <Alert
        message="Error"
        description={servicesQueryErrorMessage.message}
        type="error"
        showIcon
      />
    );
  }

  const servicesTableData: DataType[] = sessionsData!.data.map((session) => ({
    key: session.id.toString(),
    name: session.name,
    price: (session.callPrice ?? 0).toLocaleString("en-US"),
    currency: session.callPriceCurrency ?? "",
    duration: (session.callDuration ?? 0) + " minutes",
    status: session.statusName,
    sessions: session.numberOfSessions?.toString() ?? null,
    description: session.description,
    tinyUrl: session.tinyUrl,
    id: session.id,
    slug: session.slug,
    isBundle: session.isBundle ?? false,
  }));

  const handleOnRow = (record: DataType) => {
    return {
      onClick: () => {
        if (record.isBundle) {
          AnalyticsEvent(
            "Coach Services",
            `Clicked on ${record.name} bundle on service table`
          );
          navigate(`/calls/bundle/edit/${record.key}?activeTab=insights`);
        } else {
          AnalyticsEvent(
            "Coach Services",
            `Clicked on ${record.name} service on service table`
          );
          navigate(`/calls/edit/${record.key}?activeTab=insights`);
        }
      },
      style: {cursor: "pointer"},
    };
  };

  const handleNewServiceBtnClick = () => {
    AnalyticsEvent("Coach calls", `Clicked on Add new service button`);
    // showCallCategoryModal();
    navigate("/new-offering?selectOption=event");
  };

  return (
    <>
      {/* {callState !== "empty" && ( */}
      <div style={{marginTop: "4rem"}}>
        <StyledPageHeaderWrapper>
          {/* <StyledPageHeader>Services 💼</StyledPageHeader> */}
          <StyledPageHeader>Sessions</StyledPageHeader>
          <StyledCreateButtonWrapper>
            <Button
              onClick={handleNewServiceBtnClick}
              block
              size="small"
              type="primary"
              htmlType="button"
            >
              <span className="show-desktop">Create a new 1:1 session</span>
              <span className="show-mobile">
                <PlusOutlined style={{fontSize: "2.4rem"}} />
              </span>
            </Button>
          </StyledCreateButtonWrapper>
        </StyledPageHeaderWrapper>
        <Table
          onRow={handleOnRow}
          columns={columns}
          dataSource={servicesTableData}
          // pagination={tableParams.pagination}
          pagination={false}
          scroll={{x: 100}}
          // onChange={handleTableChange}
          loading={isFetchingServicesQuery}
        />
        <CustomPagination
          isPreviousData={isPreviousData}
          page={page}
          paginationMeta={toPaginationMeta(
            sessionsData as V2PaginatedResult<Session>,
            page,
            perPage
          )}
          setPage={setPage}
        />
      </div>
      {/* )} */}
    </>
  );
};

export {ServicesSection};
